import {
  addElevationProfileAtom,
  DEFAULT_ELEVATION_PROFILE_TOOL_MODE,
  elevationProfileDraftLayerIdsAtom,
  elevationProfileDraftPointsAtom,
  elevationProfileToolModeAtom,
  inferDominantLayerId,
} from '@/atoms';
import { Check, Trash, Undo } from '@mynaui/icons-react';
import { useAtomValue, useSetAtom } from 'jotai';
import { useTranslation } from 'react-i18next';

import {
  FloatingToolbar,
  FloatingToolbarButton,
  FloatingToolbarSeparator,
} from './FloatingToolbar';

const DEFAULT_BUFFER = 1.0;

/**
 * Floating top-center hint shown while the user is drawing a polyline.
 *
 * Mirrors the StockpileToolbar / AddSurfaceToolbar UX (status pill +
 * action row) via the shared `<FloatingToolbar>` primitive. The status
 * tells the user how many vertices they've placed and how to finish; the
 * actions provide Undo / Done / Cancel as button affordances for users
 * who don't know about the click-near-last-vertex / Enter / Escape
 * keyboard shortcuts.
 *
 * Renders only during `phase === 'drawing'` — once the polyline is
 * finished, `<ElevationProfilePanel>` takes over.
 */
export const ElevationProfileDrawingHint = ({ projectId }: { projectId: string }) => {
  const { t } = useTranslation('project');
  const mode = useAtomValue(elevationProfileToolModeAtom);
  const setMode = useSetAtom(elevationProfileToolModeAtom);
  const draftPoints = useAtomValue(elevationProfileDraftPointsAtom);
  const setDraftPoints = useSetAtom(elevationProfileDraftPointsAtom);
  const draftLayerIds = useAtomValue(elevationProfileDraftLayerIdsAtom);
  const setDraftLayerIds = useSetAtom(elevationProfileDraftLayerIdsAtom);
  const addProfile = useSetAtom(addElevationProfileAtom(projectId));

  if (mode.phase !== 'drawing') return null;

  const count = draftPoints.length;
  const canUndo = count > 0;
  const canFinish = count >= 2;

  const status =
    count === 0
      ? t('elevationProfile.statusPickFirst')
      : count === 1
        ? t('elevationProfile.statusPickSecond')
        : t('elevationProfile.statusPickMoreOrFinish', { count });

  const handleUndo = () => {
    if (!canUndo) return;
    // Keep both atoms in lock-step so the per-vertex layer-id index stays
    // aligned with draftPoints.
    setDraftPoints((prev) => prev.slice(0, -1));
    setDraftLayerIds((prev) => prev.slice(0, -1));
  };

  const handleDone = () => {
    if (!canFinish) return;
    const id = addProfile({
      polylinePoints: draftPoints.map((p) => [...p] as [number, number, number]),
      bufferDistance: DEFAULT_BUFFER,
      // Pre-select the layer the user picked from most. They can still
      // change it via the panel's Source dropdown.
      sourceLayerId: inferDominantLayerId(draftLayerIds),
    });
    setMode({ phase: 'panel', activeId: id });
  };

  const handleCancel = () => {
    setDraftPoints([]);
    setDraftLayerIds([]);
    setMode({ ...DEFAULT_ELEVATION_PROFILE_TOOL_MODE });
  };

  return (
    <FloatingToolbar status={status}>
      <FloatingToolbarButton
        icon={<Undo />}
        tooltip={t('elevationProfile.undo')}
        disabled={!canUndo}
        onClick={handleUndo}
      />
      <FloatingToolbarSeparator />
      <FloatingToolbarButton
        icon={<Check />}
        tooltip={t('elevationProfile.done')}
        intent='primary'
        disabled={!canFinish}
        onClick={handleDone}
      />
      <FloatingToolbarButton
        icon={<Trash />}
        tooltip={t('elevationProfile.cancel')}
        intent='danger'
        onClick={handleCancel}
      />
    </FloatingToolbar>
  );
};
